import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import { useLocation, useParams } from 'react-router-dom'
import {
  TableRow,
  TableCell,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  TextField
} from '@mui/material'
import Content from 'components/content/content'
import type { ErrorProps, ParamProps } from 'modules/types'
import {
  useDownloadVoucherUsageTokenListMutation,
  useGetVoucherUsageTokenListMutation,
  useMoveVoucherUsageTokenMutation,
  useRevokeVoucherUsageTokenMutation,
  useReactivateVoucherUsageTokenMutation
} from 'store/voucherUsage'
import Notification from 'components/notification'
import GLOBAL from 'modules/global'
import Loading from 'components/loading/loading'
import CONSTANT from 'modules/constant'
import type {
  PartialVoucherUsageTokenProps,
  PartialFormDataCapsProps
} from 'modules/partial'
import Icon from 'components/icon'
import Dialog from 'components/dialog/dialog'
import Detail from './detail/detail'

const VoucherUsageTokenList = () => {
  const params = useParams<ParamProps>()
  const location = useLocation()
  const state = location.state as { voucherName: string }

  const [getList, listResult] = useGetVoucherUsageTokenListMutation()
  const [downloadList] = useDownloadVoucherUsageTokenListMutation()
  const [moveToken, move] = useMoveVoucherUsageTokenMutation()
  const [revokeToken, revoke] = useRevokeVoucherUsageTokenMutation()
  const [reactivateToken, reactivate] =
    useReactivateVoucherUsageTokenMutation()

  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [drawerDetail, setDrawerDetail] = useState(false)
  const [dialog, setDialog] = useState(false)

  const [isRevoke, setIsRevoke] = useState(false)
  const [isReactivate, setIsReactivate] = useState(false)

  const [listChecked, setListChecked] = useState<string[]>([])
  const [changeValue, setChangeValue] = useState<string | null>(null)

  const [payload] = useState<
    PartialVoucherUsageTokenProps & PartialFormDataCapsProps
  >({
    Start: 0,
    Length: CONSTANT.DEFAULT_PAGINATION_PER_PAGE,
    Search: { value: '' },
    VoucherId: params.id || ''
  })

  /* ===================== handlers ===================== */

  const onResync = () => {
    const formData = new FormData()

    formData.append('Start', String(payload.Start))
    formData.append('Length', String(payload.Length))
    formData.append('VoucherId', String(payload.VoucherId))

    const searchValue = payload.Search?.value

    if (typeof searchValue === 'string' && searchValue.length > 0) {
      formData.append('Search', searchValue)
    }

    getList(formData)
  }

  const onDownload = (type?: string) => {
    const formData = new FormData()

    formData.append('start', '0')
    formData.append(
      'length',
      CONSTANT.DEFAULT_PAGINATION_MAXIMUM_DOWNLOAD.toString()
    )
    formData.append('VoucherId', String(payload.VoucherId))

    const searchValue = payload.Search?.value

    if (typeof searchValue === 'string' && searchValue.length > 0) {
      formData.append('Search', searchValue)
    }

    if (type) {
      formData.append('Content-Type', type)
    }

    downloadList(formData)
  }

  const onCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setListChecked(prev =>
      e.target.checked ? [...prev, value] : prev.filter(v => v !== value)
    )
  }

  const onDialogOpen = (type: 'revoke' | 'move') => {
    setIsRevoke(type === 'revoke')
    setIsReactivate(false)
    setDialog(true)
  }

  const onReactivate = () => {
    setIsRevoke(false)
    setIsReactivate(true)
    setDialog(true)
  }

  const selectedRows = listResult.data?.data.filter(row =>
    listChecked.includes(row.code)
  ) ?? []

  const canReactivate =
  selectedRows.length > 0 &&
  selectedRows.every(row =>
    String(row.status).toLowerCase() === 'revoked' ||
    row.status === '1'
  )

    // DEBUG LOG
  console.log('DEBUG canReactivate:', {
    selectedRowsLength: selectedRows.length,
    canReactivate,
    allStatuses: selectedRows.map(r => ({ code: r.code, status: r.status }))
  })

  const submitAction = () => {
    if (isRevoke) {
      return revokeToken({
        voucherTokenCodes: listChecked,
        reason: changeValue ?? undefined
      })
    }

    if (isReactivate) {
      return reactivateToken({
        voucherTokenCodes: listChecked
      })
    }

    return moveToken({
      voucherTokenCodes: listChecked,
      to: Number(changeValue)
    })
  }

  useEffect(() => {
    onResync()
  }, [payload])

  /* ===================== render ===================== */

  return (
    <>
      <Content
        title={`Voucher Usage Token List › ${state.voucherName}`}
        tableHeadTitles={[
          'Select',
          '#',
          '',
          'Code',
          'Partner',
          'City',
          'Valid From',
          'Valid Until',
          'Status'
        ]}
        onResync={onResync}
        onDownload={onDownload}
        onRevokeToken={() => onDialogOpen('revoke')}
        onMoveToken={() => onDialogOpen('move')}
        onReactivateToken={onReactivate}
        canReactivate={canReactivate}
        allChecked={listChecked}
        isLoading={listResult.isLoading}
        useCheckbox
      >
        {listResult.data?.data.map((row, index) => (
          <TableRow key={row.id}>
            <TableCell>
              <Checkbox
                value={row.code}
                checked={listChecked.includes(row.code)}
                onChange={onCheck}
              />
            </TableCell>
            <TableCell>{index + 1}</TableCell>
            <TableCell>
              <IconButton onClick={e => setAnchor(e.currentTarget)}>
                <Icon icon="MoreHoriz" />
              </IconButton>
              <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
              >
                <MenuItem onClick={() => setDrawerDetail(true)}>
                  View
                </MenuItem>
              </Menu>
              <Detail
                open={drawerDetail}
                id={row.id}
                onClose={() => setDrawerDetail(false)}
              />
            </TableCell>
            <TableCell>{row.code}</TableCell>
            <TableCell>{row.partnerName}</TableCell>
            <TableCell>{row.cities}</TableCell>
            <TableCell>
              {GLOBAL.formatDateWithTime(row.validFrom)}
            </TableCell>
            <TableCell>
              {GLOBAL.formatDateWithTime(row.validUntil)}
            </TableCell>
            <TableCell>{row.status}</TableCell>
          </TableRow>
        ))}
      </Content>

      {listResult.isLoading && <Loading />}

      <Dialog
        open={dialog}
        title={
          isRevoke
            ? 'Revoke Voucher Usage Token'
            : isReactivate
            ? 'Reactivate Voucher Usage Token'
            : 'Move Voucher Usage Token'
        }
        loading={
          isRevoke
            ? revoke.isLoading
            : isReactivate
            ? reactivate.isLoading
            : move.isLoading
        }
        onSubmit={submitAction}
        onCancel={() => {
          setDialog(false)
          setChangeValue(null)
          setIsRevoke(false)
          setIsReactivate(false)
        }}
        isDisabled={!isReactivate && isEmpty(changeValue)}
      >
        <TextField
          multiline
          rows={4}
          value={listChecked.join(',\n')}
          inputProps={{ readOnly: true }}
          fullWidth
        />

        {isRevoke && (
          <TextField
            label="Reason"
            multiline
            rows={4}
            onChange={e => setChangeValue(e.target.value)}
          />
        )}
      </Dialog>

      <Notification
        open={!reactivate.isLoading && !reactivate.isUninitialized}
        onClose={() =>
          reactivate.isError ? reactivate.reset() : window.location.reload()
        }
        isError={reactivate.isError}
        message={GLOBAL.returnExceptionMessage(
          reactivate.isError,
          reactivate.error as ErrorProps
        )}
      />
    </>
  )
}

export default VoucherUsageTokenList
